// @givmo/compliance — the organization's registered agents and foreign qualifications on screen
// (CCM-MOD5B; D-CCM-93, D-CCM-94, D-CCM-95): the section on Filings after the families (V0, V1), a
// record's panel (V2, V4, V5, V8a), the add sheet (V3, V3b), the end confirmation (V5a), the
// Calendar's row (V6), and the line a library filing shows naming a record (V8b, V6).
//
// ══ WHERE EVERY WORD COMES FROM ════════════════════════════════════════════════════════════════
//
// The words these screens are the first to say are the model's (`compliance-vendor-model.ts`), each
// named there by its ruling (D-CCM-94, D-CCM-95). The lines the module already ships and these
// screens say again are named below, under "Lines said again", each APPROVED with its source. R11's
// two labels for assistive technology are the orchestrator's call at the GO, named there too.
//
// ══ ONE PUT FOR EVERY ACT ══════════════════════════════════════════════════════════════════════
//
// The producer takes the record's whole set of facts against the revision read: adding is revision
// 0 with an id the caller chose, a correction changes one fact and sends the rest as they are, and
// ending or making current again flips `is_current`. A write that lost a race (409) re-reads, and
// every failure says the module's own "We couldn’t record that. Please try again." rather than the
// served refusal, whose words were never put to Jonathan (R9).
//
// ══ ITS ADDRESS ═════════════════════════════════════════════════════════════════════════════════
//
// A record's panel opens over Filings at `/compliance/filings?record=<id>` (R14): a search key, so
// no route identity moves. The Calendar's row, Copy link, V3b's "Open it" and V8b's link all use it.

import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  Dialog,
  FOCUS_RING,
  Field,
  Icon,
  IconButton,
  Menu,
  Select,
  Sheet,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ChevronDown, MoreHorizontal, Plus } from 'lucide-react';
import { useCompliance } from './compliance-context';
import { ComplianceDateField } from './compliance-date-field';
import {
  US_JURISDICTIONS,
  displayComplianceDate,
  displayLocalDate,
  type ObligationVendorLink,
} from './compliance-decode';
import { CORRECTIONS_AND_CHANGES } from './compliance-history-model';
import { longComplianceDate } from './compliance-panel-model';
import {
  listAllVendorRecords,
  vendorRecordQueryKey,
  vendorRecordsQueryKey,
} from './compliance-queries';
import { COMPLIANCE_ROUTES } from './compliance-routes';
import {
  ADD_A_RECORD,
  ADD_RECORD,
  ADD_RECORD_TITLE,
  CHANGE_THE_RENEWAL_DATE,
  CURRENT_AGAIN,
  END_IT,
  END_RECORD_LINE,
  END_THIS_RECORD,
  ENDED,
  KEEP_IT,
  LINK_THEM,
  NO_DATE_LINE,
  NO_DATE_RECORDED,
  NO_RECORDS,
  OPEN_IT,
  OWN_RECORD_LINE,
  PROVIDER,
  RECORD_COLUMNS,
  RECORD_KIND_NAMES,
  RECORD_TYPE,
  RECORDED_COST,
  RECORDS_SECTION_LINE,
  RECORDS_SECTION_TITLE,
  REMINDER_LINE,
  RENEWAL_DATE,
  SOURCE_NOTE,
  THE_DATE_YOU_RECORDED,
  UNLINK,
  YOUR_DATE,
  alreadyIncludesLine,
  currentRecordOf,
  decodeVendorDetail,
  decodeVendorRecords,
  displayRecordedCost,
  endRecordTitle,
  endedDay,
  endedLine,
  filingsAlsoIncludeLine,
  jurisdictionName,
  linkedRecordEndedLine,
  linkedRecordTitle,
  linkedToFilingLine,
  linkedToRecordLine,
  parseRecordedCost,
  recordsAlsoIncludeLine,
  recordsNaming,
  sectionRecords,
  showEndedRecords,
  vendorAddedLine,
  vendorHistory,
  vendorLinkHistory,
  vendorTitle,
  wontShowWhileEndedLine,
  type LinkAct,
  type LinkedLine,
  type VendorCalendarDate,
  type VendorRecord,
} from './compliance-vendor-model';
import type { ComplianceVendorRecordWrite } from '@givmo/api-client';

const PREFIX = 'compliance-records';

/** The search key that opens a record's panel over Filings (R14). */
export const RECORD_SEARCH_KEY = 'record';

// ── Lines said again ─────────────────────────────────────────────────────────────────────────────
//
// Each was APPROVED before these screens and ships in the module. REVIEW-41's page (D-CCM-94)
// names most of them REUSED, with the source given here; the rest are the GO's R9 to R11
// (2026-10-15 20:02 PDT), which reuse a line the filings already say.

/**
 * APPROVED, REUSED on REVIEW-41's V3: the filing add sheet's description (D-CCM-21, frames 03 and
 * 04).
 */
const ADD_SHEET_DESCRIPTION =
  'Tell us what you already know. Anything you skip can be added later.';
/**
 * APPROVED, REUSED on REVIEW-41's V3: the add sheet's label, section title and button (D-CCM-21,
 * frame 04).
 */
const JURISDICTION = 'Jurisdiction';
const NEXT_RENEWAL = 'Next renewal';
const CANCEL = 'Cancel';
/** APPROVED, REUSED on REVIEW-41's V4: the panel's forms' "Save" (with "Cancel"). */
const SAVE = 'Save';
/** APPROVED, REUSED on REVIEW-41's V2: a filing's number row (D-CCM-21, frames 18 and 20). */
const NOT_RECORDED = 'Not recorded';
const CORRECT = 'Correct';
const ADD = 'Add';
/** APPROVED, REUSED on REVIEW-41's V2: the own filing's "· Change" (D-CCM-21, frame 20). */
const CHANGE = 'Change';
/** APPROVED, REUSED on REVIEW-41's V2: the filing menu's "Copy link" (D-CCM-58's frames). */
const COPY_LINK = 'Copy link';
/**
 * APPROVED, reused by R10: a filing row's menu says "Open" to a viewer (Filings, D-CCM-21,
 * DESIGN-NOTE-1b §2.4), and the filing menu's link toasts, as Filings and the filing panel say
 * them.
 */
const OPEN = 'Open';
const LINK_COPIED = 'Link copied.';
const LINK_NOT_COPIED = 'We couldn’t copy the link. Please try again.';
/** APPROVED, reused by R9: a write that failed, as the filing panel's own record acts say it. */
const RECORD_FAILED = 'We couldn’t record that. Please try again.';
/**
 * APPROVED, REUSED on REVIEW-41's V1: the table's mark for a fact not recorded, as Filings has it.
 */
const NOT_RECORDED_MARK = '—';
/** APPROVED with REVIEW-41's V3 frame (D-CCM-94): the recorded cost field's "$" lead. */
const DOLLAR_LEAD = '$';
/** APPROVED: the hidden name for the menu column, as Filings' families' table has it (D-CCM-21). */
const ACTIONS_COLUMN = 'Actions';
/**
 * R11 (the orchestrator's GO): labels for assistive technology, the filing row's "Actions for
 * {filing}" and the filing panel's "More for this filing", said of a record.
 */
function rowMenuLabel(record: VendorRecord): string {
  return `Actions for ${vendorTitle(record)}`;
}
const PANEL_MENU_LABEL = 'More for this record';

/** The producer's limits on the organization's own text (the write model's). */
const PROVIDER_MAX_LENGTH = 200;
const SOURCE_NOTE_MAX_LENGTH = 2000;

// ── Reads and the one write ──────────────────────────────────────────────────────────────────────

/** Every record, current and ended, once the host affirms the module (as every other read waits). */
export function useVendorRecords() {
  const { orgRef, isReady, moduleStanding, operations } = useCompliance();
  const live = isReady && moduleStanding === 'available' && orgRef !== null;
  return useQuery({
    queryKey: vendorRecordsQueryKey(live ? orgRef : null),
    enabled: live,
    queryFn: async () => decodeVendorRecords(await listAllVendorRecords(operations, orgRef ?? '')),
  });
}

function useVendorDetail(vendorId: string) {
  const { orgRef, isReady, moduleStanding, operations } = useCompliance();
  const live = isReady && moduleStanding === 'available' && orgRef !== null;
  return useQuery({
    queryKey: vendorRecordQueryKey(live ? orgRef : null, vendorId),
    enabled: live,
    queryFn: async () =>
      decodeVendorDetail(await operations.getVendorRecord(orgRef ?? '', vendorId)),
  });
}

type FactChange = Partial<
  Pick<
    ComplianceVendorRecordWrite,
    'provider_name' | 'recorded_cost_usd' | 'renewal_on' | 'source_note' | 'is_current'
  >
>;

/** The record's whole set of facts as it stands, with one change, against the revision read. */
function writeBody(record: VendorRecord, change: FactChange): ComplianceVendorRecordWrite {
  return {
    record_kind: record.kind,
    jurisdiction_code: record.jurisdictionCode,
    provider_name: record.providerName ?? null,
    recorded_cost_usd: record.recordedCostUsd ?? null,
    renewal_on: record.renewalOn ?? null,
    source_note: record.sourceNote ?? null,
    is_current: record.isCurrent,
    expected_revision: record.revision,
    ...change,
  };
}

function useRecordVendor(onDone?: (vendorId: string) => void) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      vendorId,
      body,
    }: {
      vendorId: string;
      body: ComplianceVendorRecordWrite;
    }) => {
      await operations.recordVendor(orgRef ?? '', vendorId, body);
      return vendorId;
    },
    onSuccess: (vendorId) => {
      // A record moves the section, its panel, the Calendar and the filings that name it.
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      onDone?.(vendorId);
    },
    onError: (error) => {
      // Another write landed first: read the record as it stands, so the next try is on it.
      if (isGivmoError(error) && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      }
      toast.error(RECORD_FAILED);
    },
  });
}

/**
 * CCM-MOD5-RECORD-FILING-LINK (D-CCM-97 (1), (3)): say the record is that library filing, or undo
 * it. Both act at once and name the pair. A link moves the record's panel, the filing's, Filings
 * and the Calendar, so the module's reads are read again; so does a refusal (another person's link
 * landed first), so what stands shows.
 */
export function useLinkVendor() {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      vendorId,
      obligationId,
      unlink = false,
    }: {
      vendorId: string;
      obligationId: string;
      unlink?: boolean;
    }) => {
      if (unlink) await operations.unlinkVendorRecord(orgRef ?? '', vendorId, obligationId);
      else await operations.linkVendorRecord(orgRef ?? '', vendorId, obligationId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: ['compliance'] });
      toast.error(RECORD_FAILED);
    },
  });
}

/** Who made a link act, as its lines say it: the roster's name, if any, and the act's channel. */
function linkAct(
  userId: string | undefined,
  channel: string | undefined,
  day: string,
  personName: ((userId: string) => string | undefined) | undefined,
): LinkAct {
  const name = userId !== undefined ? personName?.(userId)?.trim() : undefined;
  return {
    day,
    ...(name !== undefined && name.length > 0 ? { name } : {}),
    ...(channel !== undefined ? { channel } : {}),
  };
}

/** A link inside a sentence, kept quiet like the panel's own. */
function InlineLink({
  children,
  onClick,
  href,
  testId,
}: {
  children: ReactNode;
  /** Followed in place; without it, the link is followed as a link. */
  onClick?: () => void;
  href: string;
  testId?: string;
}) {
  return (
    <a
      href={href}
      onClick={(event) => {
        event.stopPropagation();
        if (onClick === undefined) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onClick();
      }}
      className={cn('rounded-xs underline underline-offset-2 hover:text-text-strong', FOCUS_RING)}
      {...testIdProp(testId)}
    >
      {children}
    </a>
  );
}

function LinkedSentence({
  line,
  href,
  onOpen,
  className,
  testId,
  act,
  keepAfter = false,
}: {
  line: LinkedLine;
  href: string;
  onOpen?: () => void;
  className: string;
  testId: string;
  /** An act after the sentence, where V3b puts its "Open it" (L1's "Link them", L4's "Unlink"). */
  act?: { label: string; onClick: () => void; pending?: boolean };
  /** Keep who and when (and the act) on one line, so a long title wraps before "by …". */
  keepAfter?: boolean;
}) {
  const after = (
    <>
      {keepAfter ? line.after.trimStart() : line.after}
      {act !== undefined && (
        <>
          {' '}
          <QuietLink onClick={act.onClick} testId={`${testId}-act`}>
            {act.label}
          </QuietLink>
        </>
      )}
    </>
  );
  return (
    <p className={className} {...testIdProp(testId)}>
      {line.before}
      <InlineLink
        href={href}
        {...(onOpen !== undefined ? { onClick: onOpen } : {})}
        testId={`${testId}-link`}
      >
        {line.link}
      </InlineLink>
      {keepAfter ? (
        <>
          {line.after.startsWith(' ') ? ' ' : ''}
          <span className="whitespace-nowrap">{after}</span>
        </>
      ) : (
        after
      )}
    </p>
  );
}

/** Where a record's panel lives (R14), as an address a person can copy or open in a new tab. */
export function recordPanelHref(vendorId: string): string {
  return `${COMPLIANCE_ROUTES.filings}?${RECORD_SEARCH_KEY}=${encodeURIComponent(vendorId)}`;
}

// ── V0, V1: the section on Filings ───────────────────────────────────────────────────────────────

function RecordDateCell({ record }: { record: VendorRecord }) {
  if (!record.isCurrent) return <span className="text-text-secondary">{ENDED}</span>;
  if (record.renewalOn === undefined) {
    return <span className="text-text-tertiary">{NOT_RECORDED_MARK}</span>;
  }
  return (
    <div className="flex flex-col gap-2">
      <span className="tabular-nums">{displayComplianceDate(record.renewalOn)}</span>
      <span className="text-s12 text-text-tertiary">{YOUR_DATE}</span>
    </div>
  );
}

/** A record's name: its panel's address, followed in place, as a filing row's name is. */
function RecordLink({
  vendorId,
  href,
  onOpen,
  className,
  children,
  testId,
}: {
  vendorId: string;
  /** Another address than the record's panel: L5's row opens the filing it is linked to. */
  href?: string;
  onOpen: () => void;
  className: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <a
      href={href ?? recordPanelHref(vendorId)}
      onClick={(event) => {
        event.stopPropagation();
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onOpen();
      }}
      className={cn(className, FOCUS_RING)}
      {...testIdProp(testId)}
    >
      {children}
    </a>
  );
}

/** The row's menu, as the filing row's: "Open" for a viewer, "Copy link" for everyone (R10). */
function RecordRowMenu({
  record,
  canWrite,
  onOpen,
  onCopyLink,
}: {
  record: VendorRecord;
  canWrite: boolean;
  onOpen: () => void;
  onCopyLink: () => void;
}) {
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger
        saas
        render={<IconButton size="sm" tone="ghost" aria-label={rowMenuLabel(record)} />}
        testId={`${PREFIX}-row-menu-${record.vendorId}`}
      >
        <Icon icon={MoreHorizontal} size={16} aria-hidden />
      </Menu.Trigger>
      <Menu.Content saas className="min-w-[248px]">
        {!canWrite && (
          <Menu.Item saas onClick={onOpen}>
            {OPEN}
          </Menu.Item>
        )}
        <Menu.Item saas onClick={onCopyLink}>
          {COPY_LINK}
        </Menu.Item>
      </Menu.Content>
    </Menu.Root>
  );
}

function copyRecordLink(vendorId: string) {
  const link = new URL(recordPanelHref(vendorId), window.location.origin).href;
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  if (clipboard === undefined) {
    toast.error(LINK_NOT_COPIED);
    return;
  }
  clipboard.writeText(link).then(
    () => toast.success(LINK_COPIED),
    () => toast.error(LINK_NOT_COPIED),
  );
}

/**
 * The section after the families (J1 (A)): its title, its line, "Add a record" for someone who can
 * make changes, then the records on the families' column grid, or the empty line. A read still
 * running or one that failed shows no section: an empty one would say there are none.
 */
export function VendorRecordsSection({
  canWrite,
  selectedId,
  onOpen,
  onAdd,
}: {
  canWrite: boolean;
  /** The record whose panel is open, kept selected behind the sheet. */
  selectedId: string | undefined;
  onOpen: (vendorId: string) => void;
  onAdd: () => void;
}) {
  const { data: records } = useVendorRecords();
  const [showingEnded, setShowingEnded] = useState(false);
  if (records === undefined) return null;
  const { current, ended } = sectionRecords(records);
  const shown = showingEnded ? [...current, ...ended] : current;
  const onRowClick = (vendorId: string) => (event: MouseEvent<HTMLElement>) => {
    const target = event.target as HTMLElement;
    if (target.closest('a, button, [role="menu"], [role="menuitem"]')) return;
    onOpen(vendorId);
  };
  return (
    <section className="flex flex-col gap-14" {...testIdProp(PREFIX)}>
      <div className="flex flex-wrap items-center justify-between gap-12">
        <div className="flex min-w-0 flex-col gap-4">
          <h2 className="text-s15 font-w600 text-text-strong">{RECORDS_SECTION_TITLE}</h2>
          <p className="text-s13 text-text-secondary">{RECORDS_SECTION_LINE}</p>
        </div>
        {canWrite && (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
            onClick={onAdd}
            testId={`${PREFIX}-add`}
          >
            {ADD_A_RECORD}
          </Button>
        )}
      </div>
      {records.length === 0 ? (
        <Card className="px-20 py-16" testId={`${PREFIX}-empty`}>
          <p className="text-s14 text-text-secondary">{NO_RECORDS}</p>
        </Card>
      ) : (
        <>
          {/* Wide screens: the families' grid, so the columns line up down the page. */}
          {shown.length > 0 && (
            <Card className="hidden overflow-hidden md:block" testId={`${PREFIX}-table`}>
              <Table className="table-fixed">
                <THead>
                  <TR>
                    <TH className="w-[32%]">{RECORD_COLUMNS.record}</TH>
                    <TH className="w-[15%]">{RECORD_COLUMNS.cost}</TH>
                    <TH className="w-[25%]">{RECORD_COLUMNS.provider}</TH>
                    <TH className="w-[22%]">{RECORD_COLUMNS.renewal}</TH>
                    <TH className="w-[56px]">
                      <span className="sr-only">{ACTIONS_COLUMN}</span>
                    </TH>
                  </TR>
                </THead>
                <TBody>
                  {shown.map((record) => (
                    <TR
                      key={record.vendorId}
                      interactive
                      className={selectedId === record.vendorId ? 'bg-surface-active' : undefined}
                      aria-selected={selectedId === record.vendorId || undefined}
                      onClick={onRowClick(record.vendorId)}
                      testId={`${PREFIX}-row-${record.vendorId}`}
                    >
                      <TD className="py-[9px]">
                        <div className="flex flex-col gap-2">
                          <RecordLink
                            vendorId={record.vendorId}
                            onOpen={() => onOpen(record.vendorId)}
                            className="w-fit rounded-xs font-w500 text-text-strong"
                          >
                            {jurisdictionName(record.jurisdictionCode)}
                          </RecordLink>
                          <span className="text-s12 text-text-tertiary">
                            {RECORD_KIND_NAMES[record.kind]}
                          </span>
                        </div>
                      </TD>
                      <TD numeric>
                        {displayRecordedCost(record.recordedCostUsd) ?? (
                          <span className="text-text-tertiary">{NOT_RECORDED_MARK}</span>
                        )}
                      </TD>
                      <TD className="py-[9px]">
                        {record.providerName ?? (
                          <span className="text-text-tertiary">{NOT_RECORDED_MARK}</span>
                        )}
                      </TD>
                      <TD className="py-[9px]">
                        <RecordDateCell record={record} />
                      </TD>
                      <TD className="w-[56px] pr-12 pl-0 text-right">
                        <RecordRowMenu
                          record={record}
                          canWrite={canWrite}
                          onOpen={() => onOpen(record.vendorId)}
                          onCopyLink={() => copyRecordLink(record.vendorId)}
                        />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </Card>
          )}
          {/* Phone: the same records, stacked, as the families' rows are. */}
          {shown.length > 0 && (
            <Card className="overflow-hidden md:hidden" testId={`${PREFIX}-stacked`}>
              <ul>
                {shown.map((record) => {
                  const cost = displayRecordedCost(record.recordedCostUsd);
                  const quiet = [record.providerName, cost].filter(
                    (part): part is string => part !== undefined,
                  );
                  return (
                    <li
                      key={record.vendorId}
                      className="border-b border-border-subtle last:border-b-0"
                    >
                      <RecordLink
                        vendorId={record.vendorId}
                        onOpen={() => onOpen(record.vendorId)}
                        className="flex flex-col gap-4 px-16 py-14"
                        testId={`${PREFIX}-stacked-${record.vendorId}`}
                      >
                        <div className="flex items-start justify-between gap-12">
                          <div className="flex min-w-0 flex-col gap-2">
                            <span className="text-s14 font-w600 text-text-strong">
                              {jurisdictionName(record.jurisdictionCode)}
                            </span>
                            <span className="text-s12 text-text-tertiary">
                              {RECORD_KIND_NAMES[record.kind]}
                            </span>
                          </div>
                          <div className="shrink-0 text-right text-s14 text-text">
                            <RecordDateCell record={record} />
                          </div>
                        </div>
                        {quiet.length > 0 && (
                          <span className="text-s12 text-text-tertiary">{quiet.join(' · ')}</span>
                        )}
                      </RecordLink>
                    </li>
                  );
                })}
              </ul>
            </Card>
          )}
        </>
      )}
      {ended.length > 0 && !showingEnded && (
        <div>
          <button
            type="button"
            aria-expanded={false}
            onClick={() => setShowingEnded(true)}
            className={cn(
              'inline-flex items-center gap-4 rounded-xs text-s13 text-text-secondary hover:text-text-strong',
              FOCUS_RING,
            )}
            {...testIdProp(`${PREFIX}-show-ended`)}
          >
            {showEndedRecords(ended.length)}
            <Icon icon={ChevronDown} size={14} aria-hidden />
          </button>
        </div>
      )}
    </section>
  );
}

// ── V2, V4, V5, V8a: one record's panel ──────────────────────────────────────────────────────────

type FactForm = 'renewal' | 'provider' | 'cost' | 'note';

function PanelSection({
  title,
  children,
  testId,
}: {
  title: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="flex flex-col gap-10" {...testIdProp(testId)}>
      <h3 className="text-s14 font-w600 text-text-strong">{title}</h3>
      {children}
    </section>
  );
}

function QuietLink({
  children,
  onClick,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xs text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong',
        FOCUS_RING,
      )}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

function SaveCancel({
  disabled,
  pending,
  onCancel,
}: {
  disabled: boolean;
  pending: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex gap-8">
      <Button
        appearance="saas"
        tone="primary"
        size="sm"
        type="submit"
        disabled={disabled || pending}
        loading={pending}
        testId={`${PREFIX}-form-save`}
      >
        {SAVE}
      </Button>
      <Button appearance="saas" tone="ghost" size="sm" type="button" onClick={onCancel}>
        {CANCEL}
      </Button>
    </div>
  );
}

/** A fact's correction in place, as a filing's number is corrected; the date's under its own title (V4). */
function FactFormCard({
  form,
  record,
  pending,
  onSave,
  onCancel,
}: {
  form: FactForm;
  record: VendorRecord;
  pending: boolean;
  onSave: (change: FactChange) => void;
  onCancel: () => void;
}) {
  const initial =
    form === 'renewal'
      ? (record.renewalOn ?? '')
      : form === 'provider'
        ? (record.providerName ?? '')
        : form === 'cost'
          ? (record.recordedCostUsd ?? '')
          : (record.sourceNote ?? '');
  const [value, setValue] = useState(initial);
  // What the form writes: a cost read as the producer takes it (a leading "$" and thousands
  // separators read away, `undefined` where it can't be read), every other fact as typed, trimmed.
  const written = form === 'cost' ? parseRecordedCost(value) : value.trim();
  const ready = written !== undefined && written.length > 0 && written !== initial.trim();
  const change = (): FactChange => {
    const fact = written ?? '';
    return form === 'renewal'
      ? { renewal_on: fact }
      : form === 'provider'
        ? { provider_name: fact }
        : form === 'cost'
          ? { recorded_cost_usd: fact }
          : { source_note: fact };
  };
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) onSave(change());
      }}
    >
      <div
        className="flex flex-col gap-14 rounded-lg border border-border-subtle p-16"
        {...testIdProp(`${PREFIX}-form-${form}`)}
      >
        {form === 'renewal' ? (
          <>
            <p className="text-s14 font-w600 text-text-strong">{CHANGE_THE_RENEWAL_DATE}</p>
            <ComplianceDateField
              label={RENEWAL_DATE}
              value={value}
              onChange={setValue}
              testId={`${PREFIX}-form-renewal-field`}
            />
            <p className="text-s12 text-text-tertiary">{REMINDER_LINE}</p>
          </>
        ) : (
          <Field
            label={form === 'provider' ? PROVIDER : form === 'cost' ? RECORDED_COST : SOURCE_NOTE}
            value={value}
            autoFocus
            maxLength={form === 'note' ? SOURCE_NOTE_MAX_LENGTH : PROVIDER_MAX_LENGTH}
            {...(form === 'cost'
              ? {
                  inputMode: 'decimal' as const,
                  leading: <span className="text-s14 text-text-tertiary">{DOLLAR_LEAD}</span>,
                  containerClassName: 'max-w-[240px]',
                }
              : {})}
            onChange={(event) => setValue(event.target.value)}
            testId={`${PREFIX}-form-${form}-field`}
          />
        )}
        <SaveCancel disabled={!ready} pending={pending} onCancel={onCancel} />
      </div>
    </form>
  );
}

/** V5a: from the panel's menu, as stopping a filing's tracking asks (S2, D-CCM-58). */
function EndRecordDialog({
  title,
  linkedFiling,
  open,
  pending,
  onEnd,
  onClose,
}: {
  title: string;
  /** D-CCM-97 (choice 9): the filing a dated record is linked to, which loses its date. */
  linkedFiling?: string;
  open: boolean;
  pending: boolean;
  onEnd: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {open && (
        <Dialog.Content
          width={440}
          className="flex flex-col gap-16"
          testId={`${PREFIX}-end-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {endRecordTitle(title)}
          </Dialog.Title>
          <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {END_RECORD_LINE}
            {linkedFiling !== undefined && ` ${wontShowWhileEndedLine(linkedFiling)}`}
          </Dialog.Description>
          <div className="flex flex-wrap gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={pending}
              onClick={onEnd}
              testId={`${PREFIX}-end-confirm`}
            >
              {END_IT}
            </Button>
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              disabled={pending}
              onClick={onClose}
              testId={`${PREFIX}-end-keep`}
            >
              {KEEP_IT}
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

/**
 * V8a: a current foreign qualification's panel names the confirmed library filing of its kind in its
 * jurisdiction, under "Added by". Its title is the filing's own, as Filings names it.
 */
function FilingsNamedLine({
  record,
  filingTitle,
  onOpenFiling,
}: {
  record: VendorRecord;
  filingTitle: (obligationId: string) => string | undefined;
  onOpenFiling: (obligationId: string) => void;
}) {
  const { canWrite, personName } = useCompliance();
  const link = useLinkVendor();
  const { data: records } = useVendorRecords();
  const linked = record.filingLink;
  // L4 (D-CCM-97): a linked record names its filing in V8a's place, with who linked them, and
  // "Unlink" for a writer. The link stands through an end (choice 9), so an ended record keeps it.
  if (linked !== undefined) {
    const title = filingTitle(linked.obligationId);
    const day = displayLocalDate(linked.linkedAt);
    if (title === undefined || day === '—') return null;
    return (
      <LinkedSentence
        line={linkedToFilingLine(
          title,
          linkAct(linked.linkedByUserId, linked.linkedChannel, day, personName),
        )}
        href={COMPLIANCE_ROUTES.filing(linked.obligationId)}
        onOpen={() => onOpenFiling(linked.obligationId)}
        className="pt-6 text-s13 leading-h15 text-text-secondary"
        testId={`${PREFIX}-panel-linked-${linked.obligationId}`}
        keepAfter
        {...(canWrite === true
          ? {
              act: {
                label: UNLINK,
                onClick: () =>
                  link.mutate({
                    vendorId: record.vendorId,
                    obligationId: linked.obligationId,
                    unlink: true,
                  }),
              },
            }
          : {})}
      />
    );
  }
  if (!record.isCurrent) return null;
  return (
    <>
      {record.libraryFilingIds.map((obligationId) => {
        const title = filingTitle(obligationId);
        if (title === undefined) return null;
        // L1 (D-CCM-97 (1), (6)): a writer may link a current foreign qualification to the filing,
        // while that filing has no other record's link.
        const linkable =
          canWrite === true &&
          record.kind === 'foreign_qualification' &&
          records !== undefined &&
          !records.some((other) => other.filingLink?.obligationId === obligationId);
        return (
          <LinkedSentence
            key={obligationId}
            line={filingsAlsoIncludeLine(title)}
            href={COMPLIANCE_ROUTES.filing(obligationId)}
            onOpen={() => onOpenFiling(obligationId)}
            className="pt-6 text-s13 leading-h15 text-text-secondary"
            testId={`${PREFIX}-panel-filing-${obligationId}`}
            {...(linkable
              ? {
                  act: {
                    label: LINK_THEM,
                    onClick: () => link.mutate({ vendorId: record.vendorId, obligationId }),
                  },
                }
              : {})}
          />
        );
      })}
    </>
  );
}

/**
 * One record's panel, shaped like an own filing's (frame 20): its renewal date, the line saying the
 * record is the organization's own and unchecked with who added it, its facts, then what changed.
 * It draws from the list's row at once, and adds the history when the record's own read lands; a
 * record the list doesn't hold (an old link) closes the panel.
 */
export function VendorRecordPanel({
  vendorId,
  filingTitle,
  onClose,
  onOpenFiling,
}: {
  vendorId: string;
  /** A library filing's title as Filings names it, for V8a; nothing for one the page doesn't hold. */
  filingTitle: (obligationId: string) => string | undefined;
  onClose: () => void;
  onOpenFiling: (obligationId: string) => void;
}) {
  const { canWrite, personName } = useCompliance();
  const writable = canWrite === true;
  const list = useVendorRecords();
  const detail = useVendorDetail(vendorId);
  const [form, setForm] = useState<FactForm | undefined>(undefined);
  const [ending, setEnding] = useState(false);
  const write = useRecordVendor(() => {
    setForm(undefined);
    setEnding(false);
  });
  const listed = list.data?.find((record) => record.vendorId === vendorId);
  // The row carries the confirmed filings of its kind; the record's own read carries the rest.
  const fromDetail = detail.data?.vendor;
  const openLink = fromDetail?.filingLink ?? listed?.filingLink;
  const record =
    fromDetail !== undefined
      ? {
          ...fromDetail,
          libraryFilingIds: listed?.libraryFilingIds ?? [],
          ...(openLink !== undefined ? { filingLink: openLink } : {}),
        }
      : listed;
  const events = detail.data?.events ?? [];
  const nameOf = (userId: string) => personName?.(userId);
  // A record the list doesn't hold and its own read can't find (an old or mistyped link) is not the
  // organization's: back to Filings, where its records are.
  const missing = list.data !== undefined && listed === undefined && detail.isError;
  useEffect(() => {
    if (missing) onClose();
  }, [missing, onClose]);
  const title = record !== undefined ? vendorTitle(record) : '';
  // D-CCM-97 (choice 9): ending a dated, linked record takes the filing's date away; V5 says so.
  const endLinkedFiling =
    record?.filingLink !== undefined && record.renewalOn !== undefined
      ? filingTitle(record.filingLink.obligationId)
      : undefined;
  const save = (change: FactChange) => {
    if (record !== undefined) write.mutate({ vendorId, body: writeBody(record, change) });
  };

  let body: ReactNode;
  if (record === undefined) {
    body = <Skeleton className="h-[320px] w-full" {...testIdProp(`${PREFIX}-panel-loading`)} />;
  } else {
    const added = vendorAddedLine(record, events, nameOf);
    // The record's links join its changes, newest first (L4's entries and their undoing).
    const entries = [
      ...vendorHistory(events, nameOf),
      ...vendorLinkHistory(detail.data?.filingLinks ?? [], filingTitle, nameOf),
    ].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
    const ended = record.isCurrent ? undefined : endedLine(events, nameOf);
    const factRow = (fact: Exclude<FactForm, 'renewal'>, label: string, shown?: string) => (
      <PanelSection title={label} testId={`${PREFIX}-panel-${fact}`}>
        {form === fact ? (
          <FactFormCard
            form={fact}
            record={record}
            pending={write.isPending}
            onSave={save}
            onCancel={() => setForm(undefined)}
          />
        ) : (
          <div className="flex items-center gap-12">
            {shown !== undefined ? (
              <span className="min-w-0 text-s14 text-text wrap-anywhere">{shown}</span>
            ) : (
              <span className="text-s14 text-text-tertiary">{NOT_RECORDED}</span>
            )}
            {writable && record.isCurrent && (
              <QuietLink onClick={() => setForm(fact)} testId={`${PREFIX}-panel-${fact}-edit`}>
                {shown !== undefined ? CORRECT : ADD}
              </QuietLink>
            )}
          </div>
        )}
      </PanelSection>
    );
    body = (
      <>
        <PanelSection title={RENEWAL_DATE} testId={`${PREFIX}-panel-renewal`}>
          {!record.isCurrent ? (
            <div
              className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12"
              {...testIdProp(`${PREFIX}-panel-ended`)}
            >
              <p className="text-s14 font-w600 text-text-strong">{ENDED}</p>
              <div className="flex flex-col gap-2">
                {ended !== undefined && (
                  <p className="text-s13 leading-h15 text-text-secondary">{ended}</p>
                )}
                <p className="text-s13 leading-h15 text-text-secondary">{END_RECORD_LINE}</p>
              </div>
            </div>
          ) : form === 'renewal' ? (
            <FactFormCard
              form="renewal"
              record={record}
              pending={write.isPending}
              onSave={save}
              onCancel={() => setForm(undefined)}
            />
          ) : (
            <div
              className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12"
              {...testIdProp(`${PREFIX}-panel-date`)}
            >
              <p
                className={
                  record.renewalOn !== undefined
                    ? 'text-s16 font-w600 tabular-nums text-text-strong'
                    : 'text-s14 font-w600 text-text-strong'
                }
              >
                {record.renewalOn !== undefined
                  ? longComplianceDate(record.renewalOn)
                  : NO_DATE_RECORDED}
              </p>
              <p className="text-s13 leading-h15 text-text-secondary">
                {record.renewalOn !== undefined ? THE_DATE_YOU_RECORDED : NO_DATE_LINE}
                {writable && (
                  <>
                    {record.renewalOn !== undefined ? ' · ' : ' '}
                    <QuietLink
                      onClick={() => setForm('renewal')}
                      testId={`${PREFIX}-panel-renewal-edit`}
                    >
                      {record.renewalOn !== undefined ? CHANGE : ADD}
                    </QuietLink>
                  </>
                )}
              </p>
              {record.renewalOn !== undefined && (
                <p className="text-s12 leading-h15 text-text-tertiary">{REMINDER_LINE}</p>
              )}
            </div>
          )}
        </PanelSection>
        {!record.isCurrent && writable && (
          <div className="-mt-14">
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              loading={write.isPending}
              onClick={() =>
                write.mutate({ vendorId, body: writeBody(record, { is_current: true }) })
              }
              testId={`${PREFIX}-panel-current-again`}
            >
              {CURRENT_AGAIN}
            </Button>
          </div>
        )}
        <div className="-mt-14 flex flex-col gap-4">
          <p className="text-s13 leading-h15 text-text-secondary">{OWN_RECORD_LINE}</p>
          {added !== undefined && (
            <p className="text-s12 text-text-tertiary" {...testIdProp(`${PREFIX}-panel-added`)}>
              {added}
            </p>
          )}
          <FilingsNamedLine record={record} filingTitle={filingTitle} onOpenFiling={onOpenFiling} />
        </div>
        {factRow('provider', PROVIDER, record.providerName)}
        {factRow('cost', RECORDED_COST, displayRecordedCost(record.recordedCostUsd))}
        {factRow('note', SOURCE_NOTE, record.sourceNote)}
        {entries.length > 0 && (
          <PanelSection title={CORRECTIONS_AND_CHANGES} testId={`${PREFIX}-panel-changes`}>
            <ul className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-subtle">
              {entries.map((entry) => (
                <li key={entry.key} className="flex flex-col gap-2 px-16 py-10">
                  <p className="text-s13 text-text wrap-anywhere">{entry.what}</p>
                  <p className="text-s12 leading-h15 text-text-tertiary wrap-anywhere">
                    {entry.how}
                  </p>
                </li>
              ))}
            </ul>
          </PanelSection>
        )}
      </>
    );
  }

  return (
    <Sheet.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Sheet.Content
        size="lg"
        testId={`${PREFIX}-panel`}
        initialFocus={false}
        aria-label={title || undefined}
      >
        <Sheet.Header>
          <div className="flex min-w-0 flex-1 items-start justify-between gap-12">
            <div className="flex min-w-0 flex-col gap-2">
              <span className="text-s12 font-w500 text-text-tertiary">{RECORDS_SECTION_TITLE}</span>
              <Sheet.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
                {title}
              </Sheet.Title>
            </div>
            {record !== undefined && (
              <Menu.Root modal={false}>
                <Menu.Trigger
                  saas
                  render={<IconButton size="sm" tone="ghost" aria-label={PANEL_MENU_LABEL} />}
                  testId={`${PREFIX}-panel-menu`}
                >
                  <Icon icon={MoreHorizontal} size={18} aria-hidden />
                </Menu.Trigger>
                <Menu.Content saas className="min-w-[248px]">
                  <Menu.Item saas onClick={() => copyRecordLink(vendorId)}>
                    {COPY_LINK}
                  </Menu.Item>
                  {writable && record.isCurrent && (
                    <>
                      <Menu.Separator className="mx-4 my-4 bg-border-subtle" />
                      <Menu.Item saas onClick={() => setEnding(true)}>
                        {END_THIS_RECORD}
                      </Menu.Item>
                    </>
                  )}
                </Menu.Content>
              </Menu.Root>
            )}
          </div>
        </Sheet.Header>
        <Sheet.Body className="flex flex-col gap-28">{body}</Sheet.Body>
        {record !== undefined && (
          <EndRecordDialog
            title={title}
            {...(endLinkedFiling !== undefined ? { linkedFiling: endLinkedFiling } : {})}
            open={ending}
            pending={write.isPending}
            onEnd={() => write.mutate({ vendorId, body: writeBody(record, { is_current: false }) })}
            onClose={() => setEnding(false)}
          />
        )}
      </Sheet.Content>
    </Sheet.Root>
  );
}

// ── V3, V3b: recording one ───────────────────────────────────────────────────────────────────────

/**
 * Shaped like the filing add sheet (frame 04): the jurisdiction and the type side by side, then the
 * facts, then "Next renewal". A current record of the chosen type and jurisdiction is named and
 * linked, and another may still be added: the producer allows it (V3b).
 */
export function AddVendorRecordSheet({
  open,
  onClose,
  onAdded,
  onOpenRecord,
}: {
  open: boolean;
  onClose: () => void;
  onAdded: (vendorId: string) => void;
  onOpenRecord: (vendorId: string) => void;
}) {
  return (
    <Sheet.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {open && (
        <Sheet.Content size="lg" testId={`${PREFIX}-add-sheet`} initialFocus={false}>
          <Sheet.Header title={ADD_RECORD_TITLE} description={ADD_SHEET_DESCRIPTION} />
          <AddVendorRecordForm onClose={onClose} onAdded={onAdded} onOpenRecord={onOpenRecord} />
        </Sheet.Content>
      )}
    </Sheet.Root>
  );
}

function AddVendorRecordForm({
  onClose,
  onAdded,
  onOpenRecord,
}: {
  onClose: () => void;
  onAdded: (vendorId: string) => void;
  onOpenRecord: (vendorId: string) => void;
}) {
  const [jurisdiction, setJurisdiction] = useState('');
  const [kind, setKind] = useState('');
  const [provider, setProvider] = useState('');
  const [cost, setCost] = useState('');
  const [note, setNote] = useState('');
  const [renewal, setRenewal] = useState('');
  // The id is the caller's to choose, once per sheet, so a retry of the same add is the same record.
  const [vendorId] = useState(() => crypto.randomUUID());
  const { data: records } = useVendorRecords();
  const add = useRecordVendor(onAdded);
  const same = records !== undefined ? currentRecordOf(records, kind, jurisdiction) : undefined;
  // The cost as the producer takes it (a leading "$" and thousands separators read away), `null`
  // where nothing was typed, and `undefined` where what was typed can't be read, which holds the
  // add.
  const recordedCost = cost.trim() === '' ? null : parseRecordedCost(cost);
  const ready =
    jurisdiction !== '' &&
    (kind === 'registered_agent' || kind === 'foreign_qualification') &&
    recordedCost !== undefined;
  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready || (kind !== 'registered_agent' && kind !== 'foreign_qualification')) return;
        add.mutate({
          vendorId,
          body: {
            record_kind: kind,
            jurisdiction_code: jurisdiction,
            provider_name: provider.trim() || null,
            recorded_cost_usd: recordedCost ?? null,
            renewal_on: renewal || null,
            source_note: note.trim() || null,
            is_current: true,
            expected_revision: 0,
          },
        });
      }}
    >
      <Sheet.Body className="flex flex-col gap-16">
        <div className="flex flex-col gap-10">
          <div className="grid grid-cols-1 gap-16 sm:grid-cols-2">
            <Select
              appearance="saas"
              label={JURISDICTION}
              value={jurisdiction === '' ? null : jurisdiction}
              onValueChange={(next) => setJurisdiction(next ?? '')}
              options={US_JURISDICTIONS.map(({ code, name }) => ({ value: code, label: name }))}
              testId={`${PREFIX}-add-jurisdiction`}
            />
            <Select
              appearance="saas"
              label={RECORD_TYPE}
              value={kind === '' ? null : kind}
              onValueChange={(next) => setKind(next ?? '')}
              options={[
                { value: 'registered_agent', label: RECORD_KIND_NAMES.registered_agent },
                { value: 'foreign_qualification', label: RECORD_KIND_NAMES.foreign_qualification },
              ]}
              testId={`${PREFIX}-add-kind`}
            />
          </div>
          {same !== undefined && (
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-add-same`)}
            >
              {alreadyIncludesLine(vendorTitle(same))}{' '}
              <InlineLink
                href={recordPanelHref(same.vendorId)}
                onClick={() => onOpenRecord(same.vendorId)}
                testId={`${PREFIX}-add-same-open`}
              >
                {OPEN_IT}
              </InlineLink>
            </p>
          )}
        </div>
        <Field
          label={PROVIDER}
          value={provider}
          maxLength={PROVIDER_MAX_LENGTH}
          containerClassName="max-w-[320px]"
          onChange={(event) => setProvider(event.target.value)}
          testId={`${PREFIX}-add-provider`}
        />
        <Field
          label={RECORDED_COST}
          value={cost}
          inputMode="decimal"
          containerClassName="max-w-[240px]"
          leading={<span className="text-s14 text-text-tertiary">{DOLLAR_LEAD}</span>}
          onChange={(event) => setCost(event.target.value)}
          testId={`${PREFIX}-add-cost`}
        />
        <Field
          label={SOURCE_NOTE}
          value={note}
          maxLength={SOURCE_NOTE_MAX_LENGTH}
          onChange={(event) => setNote(event.target.value)}
          testId={`${PREFIX}-add-note`}
        />
        <section className="flex flex-col gap-12 pt-8">
          <h3 className="text-s14 font-w600 text-text-strong">{NEXT_RENEWAL}</h3>
          <div className="flex flex-col gap-6">
            <ComplianceDateField
              label={RENEWAL_DATE}
              value={renewal}
              onChange={setRenewal}
              testId={`${PREFIX}-add-date`}
            />
            <p className="text-s12 text-text-tertiary">{REMINDER_LINE}</p>
          </div>
        </section>
      </Sheet.Body>
      <Sheet.Footer>
        <Button appearance="saas" tone="secondary" type="button" onClick={onClose}>
          {CANCEL}
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          type="submit"
          disabled={!ready || add.isPending}
          loading={add.isPending}
          testId={`${PREFIX}-add-submit`}
        >
          {ADD_RECORD}
        </Button>
      </Sheet.Footer>
    </form>
  );
}

// ── V6: a record's date on the Calendar ──────────────────────────────────────────────────────────

/** The row, in the Calendar's own grid: the date, the record's title, its basis, the provider. */
export function VendorCalendarRow({
  date,
  onOpen,
}: {
  date: VendorCalendarDate;
  onOpen: () => void;
}) {
  return (
    <li
      onClick={(event) => {
        const target = event.target as HTMLElement;
        if (target.closest('a, button')) return;
        onOpen();
      }}
      className="grid cursor-pointer grid-cols-1 items-start gap-y-4 px-20 py-16 hover:bg-surface-hover sm:grid-cols-[112px_minmax(0,1fr)] sm:gap-x-20 sm:gap-y-0"
      {...testIdProp(`compliance-calendar-date-${date.key}`)}
    >
      <div className="flex flex-col gap-2">
        <p className="text-s14 font-w600 tabular-nums text-text-strong">{date.dateLabel}</p>
      </div>
      <div className="flex min-w-0 flex-col gap-4">
        <RecordLink
          vendorId={date.record.vendorId}
          // L5 (D-CCM-97 (4)): a linked filing's row is the filing's, titled by it; its title opens it.
          {...(date.filingId !== undefined
            ? { href: COMPLIANCE_ROUTES.filing(date.filingId) }
            : {})}
          onOpen={onOpen}
          className="w-fit rounded-xs text-s14 font-w600 text-text-strong hover:underline hover:underline-offset-2"
          testId={`compliance-calendar-date-name-${date.key}`}
        >
          {date.title}
        </RecordLink>
        <p className="text-s13 text-text-secondary">{date.basis}</p>
        {date.lines.map((line) => (
          <p key={line} className="text-s12 text-text-tertiary">
            {line}
          </p>
        ))}
      </div>
    </li>
  );
}

// ── V8b, V6: a library filing names the records of its kind ──────────────────────────────────────

/**
 * "Your records also include {record}, renewing {date}." under a library filing that has no date
 * (V8b, in its Next due block; V6, under its row in "Not supported yet"), one line for each current
 * record that names it. Nothing while the records read, or where none does.
 */
export function RecordsNamingLine({
  obligationId,
  quiet = false,
  onOpenRecord,
}: {
  obligationId: string;
  /** The Calendar's row speaks in its quiet lines' size. */
  quiet?: boolean;
  /** Opens the record's panel in place; without it, the link is followed as a link. */
  onOpenRecord?: (vendorId: string) => void;
}) {
  const { canWrite } = useCompliance();
  const link = useLinkVendor();
  const { data: records } = useVendorRecords();
  if (records === undefined) return null;
  const naming = recordsNaming(records, obligationId);
  if (naming.length === 0) return null;
  return (
    <>
      {naming.map((record) => (
        <LinkedSentence
          key={record.vendorId}
          line={recordsAlsoIncludeLine(record)}
          href={recordPanelHref(record.vendorId)}
          {...(onOpenRecord !== undefined ? { onOpen: () => onOpenRecord(record.vendorId) } : {})}
          className={cn(
            'leading-h15',
            quiet ? 'text-s12 text-text-tertiary' : 'text-s13 text-text-secondary',
          )}
          testId={`${PREFIX}-naming-${obligationId}-${record.vendorId}`}
          // L1 on the filing (D-CCM-97 (1), L7a): a writer links a foreign qualification without a
          // link of its own; never on the Calendar's quiet line.
          {...(!quiet &&
          canWrite === true &&
          record.kind === 'foreign_qualification' &&
          record.filingLink === undefined
            ? {
                act: {
                  label: LINK_THEM,
                  onClick: () => link.mutate({ vendorId: record.vendorId, obligationId }),
                },
              }
            : {})}
        />
      ))}
    </>
  );
}

/**
 * L3 (D-CCM-97): the line a linked library filing shows in V8b's place, naming its record and who
 * linked them, the record's title opening its panel; quiet under the Calendar's row (4.4 (b)).
 * While the record is ended, it says so with the day it ended, read from the record's history
 * (words only, choice 9); until that read lands, nothing.
 */
export function LinkedRecordLine({
  obligationId,
  link,
  quiet = false,
  onOpenRecord,
}: {
  obligationId: string;
  link: ObligationVendorLink;
  quiet?: boolean;
  onOpenRecord?: (vendorId: string) => void;
}) {
  const { personName } = useCompliance();
  const title = linkedRecordTitle(link);
  const className = cn(
    'leading-h15',
    quiet ? 'text-s12 text-text-tertiary' : 'text-s13 text-text-secondary',
  );
  const opener = onOpenRecord !== undefined ? { onOpen: () => onOpenRecord(link.vendorId) } : {};
  if (!link.isCurrent) {
    return (
      <EndedLinkedRecordLine
        link={link}
        title={title}
        className={className}
        testId={`${PREFIX}-linked-${obligationId}`}
        {...opener}
      />
    );
  }
  const day = displayLocalDate(link.linkedAt);
  if (day === '—') return null;
  return (
    <LinkedSentence
      line={linkedToRecordLine(
        title,
        linkAct(link.linkedByUserId, link.linkedChannel, day, personName),
      )}
      href={recordPanelHref(link.vendorId)}
      {...opener}
      className={className}
      testId={`${PREFIX}-linked-${obligationId}`}
      keepAfter
    />
  );
}

function EndedLinkedRecordLine({
  link,
  title,
  className,
  testId,
  onOpen,
}: {
  link: ObligationVendorLink;
  title: string;
  className: string;
  testId: string;
  onOpen?: () => void;
}) {
  const detail = useVendorDetail(link.vendorId);
  const ended = endedDay(detail.data?.events ?? []);
  if (ended === undefined) return null;
  return (
    <LinkedSentence
      line={linkedRecordEndedLine(title, ended)}
      href={recordPanelHref(link.vendorId)}
      {...(onOpen !== undefined ? { onOpen } : {})}
      className={className}
      testId={testId}
      keepAfter
    />
  );
}
